import React, { useEffect, useRef } from 'react'
import links from 'links'
import { useDevice } from 'device'
import dayjs, { getElapsedTime } from 'date'
import { useAb, useFt } from 'hooks'
import { usePathname, useSearchParams } from 'router'
import { openModal } from 'modals'
import cookieStorage from 'cookie-storage'
import { constants } from 'helpers'

import { useStrapiWSTOBrandSlug, useStrapiWSTOModalWithProductsOptions } from 'modules/brands'
import { usePrivateSale } from 'modules/products'
import { useLimitedDropSale } from 'modules/limitedDrops'
import { useUnratedProduct } from 'modules/recommendations'
import { useCandleSubscription, useCaseSubscription, useDriftSubscription, useSubscription } from 'modules/subscription'
import { useReferralInvites, useSetPopupShown, useShownPopups, useUser } from 'modules/user'


// ATTN all loading should be processed inside modals, because we should show only one modal at once
const SubscriptionModals: React.FunctionComponent = () => {
  // data for modals
  const pathname = usePathname()
  const { isMobile } = useDevice()
  const [ { autoTestSkip } ] = useSearchParams()
  const modalWasOpenRef = useRef(Boolean(autoTestSkip))

  const isCandleSubscriptionEnabled = useFt(constants.features.candleSubscription)
  const isDriftSubscriptionEnabled = useFt(constants.features.driftSubscription)
  const isSaksCollaborationEnabled = useFt(constants.features.collaborationSaks)
  const isMobileAppEntrypointModalEnabled = useFt(constants.features.mobileAppEntryPoint)
  const isLimitedDropsEnabled = useFt(constants.features.limitedDrops)
  const isDriftEcommercePromoEnabled = useFt(constants.features.driftEcommercePromo)
  const isQueueUpgradePopupEnabled = useAb(constants.abTests.upgradePopup) === 'b'

  const strapiWSTOBrandSlug = useStrapiWSTOBrandSlug()
  const strapiWSTOModalWithProductsOptions = useStrapiWSTOModalWithProductsOptions()
  const isStrapiWSTOEnabled = Boolean(strapiWSTOBrandSlug)

  const { user, isLoggedIn } = useUser()
  const { subscription } = useSubscription()
  const { candleSubscription } = useCandleSubscription()
  const { caseSubscription } = useCaseSubscription()
  const { driftSubscription } = useDriftSubscription()
  const { shownPopups } = useShownPopups()
  const setPopupShown = useSetPopupShown()
  const { privateSale, isFetching } = usePrivateSale()
  const { product: unratedProduct, isFetching: isUnratedProductFetching } = useUnratedProduct({ skip: !isLoggedIn })
  const { data: limitedDrop, isFetching: isLimitedDropFetching } = useLimitedDropSale()
  const { stats: { available: availableReferralInvites } } = useReferralInvites()

  useEffect(() => {
    if (modalWasOpenRef.current) {
      return
    }

    // loading state
    if (!isLoggedIn || !user || !subscription || !shownPopups || isFetching || isUnratedProductFetching || isLimitedDropFetching) {
      return
    }

    // some modals should be opened only on main page
    const isMainPage = pathname === links.subscription.main

    /**
     * ATTN if you already have a modal in Modals file, just import it`s module and open.
     *  Otherwise, import a module and use setActiveModal to load and open it.
     */

    const lastShipmentReviewModal = (priority: number) => {
      /**
       If a customer closes the popup we will show it again 2 weeks after.
       If a customer closes the second popup we don’t show it again.
       New “Delivery” event will trigger a new popup.
       */
      if (!unratedProduct) {
        return
      }

      const popupId = `lastShipmentReview_${unratedProduct.id}`
      const { count = 0, lastUpdated = 0 } = shownPopups[popupId] || {}
      const lastUpdateTime = (new Date(lastUpdated)).getTime() + 14 * 24 * 60 * 60 * 1000 // 14 days between shows

      const isVisible = (
        isMainPage
        && (
          count === 0 || (count === 1 && Date.now() > lastUpdateTime)
        )
      )

      if (isVisible) {
        const onReviewModalClose = () => setPopupShown(popupId)

        if (isMobile) {
          openModal('lastShipmentReviewModal', {
            priority,
            product: unratedProduct,
            onClose: onReviewModalClose,
          })
        }
        else {
          openModal('productReviewModal', {
            flow: 'Review popup',
            placement: 'Page body',
            productId: unratedProduct.id,
            onClose: onReviewModalClose,
          })
        }

        return true
      }
    }

    const inviteModal = (priority: number) => {
      const isPathnameAcceptable = (
        pathname === links.subscription.perfumes
        || pathname === links.subscription.colognes
        || pathname === links.subscription.main
      )

      const isReferralItemVisible = (
        isPathnameAcceptable && availableReferralInvites > 0
      )

      if (isReferralItemVisible) {
        const { lastUpdated } = shownPopups.inviteModal || {}

        const lastUpdatedDate = dayjs(lastUpdated || 0)
        const now = dayjs()

        // open each month after 2nd, because we add credits after the 2nd every month
        if (!lastUpdated || now.isAfter(lastUpdatedDate, 'month') && now.date() >= 2) {
          openModal('inviteModal', {
            priority,
            onClose: () => setPopupShown('inviteModal'),
          })

          return true
        }
      }
    }

    const cardUpdateModal = (priority: number) => {
      const isShownInSession = cookieStorage.getItem(constants.cookieNames.isCardUpdateModalShown)

      const isVisible = (
        isMainPage
        && subscription.isUnpaid
        && !isShownInSession
      )

      if (isVisible) {
        openModal('cardUpdateModal', {
          priority,
          flow: 'Website visit',
        })

        return true
      }
    }

    const firstTimeVisitModal = (priority: number) => {
      // This modal should be only visible on mobile as a result of CRO-97 ab test
      const isVisible = (
        isMobile
        && isMainPage
        && subscription.hasNeverSubscribed
        && !shownPopups?.firstTimeVisitModal?.count
      )

      if (isVisible) {
        openModal('firstTimeVisitModal', { priority })
        return true
      }
    }

    const candleSubscriptionPromoModalShownCount = shownPopups?.candleSubscriptionPromoModal?.count || 0

    const candleSubscriptionPromoModal = (priority: number) => {
      const lastUpdated = shownPopups?.candleSubscriptionPromoModal?.lastUpdated

      const isVisible = (
        isMainPage
        && isCandleSubscriptionEnabled
        && candleSubscription?.isEnabled
        && !candleSubscription?.isSelected
        && candleSubscriptionPromoModalShownCount < 3
        && (!lastUpdated || getElapsedTime(lastUpdated, 'hours') >= 24)
      )

      if (isVisible) {
        openModal('candleSubscriptionPromoModal', { priority })
        return true
      }
    }

    const privateSaleModal = (priority: number) => {
      if (!privateSale) {
        return
      }

      const popupName = `privateSale${new Date(privateSale.startDate).getTime()}`
      const wasNotShownBefore = !shownPopups?.[popupName]

      const isVisible = (
        isLoggedIn
        && privateSale.timeLeft > 5000
        && wasNotShownBefore
      )

      if (isVisible) {
        openModal('privateSaleModal', { priority, popupName })
        return true
      }
    }

    const tossInModal = (priority: number) => {
      const month = new Date().getMonth()
      const year = new Date().getFullYear()
      const lastUpdated = shownPopups?.tossInModal?.lastUpdated

      let isVisible = subscription?.canApplyTossInOffer

      if (lastUpdated) {
        const popupMonth = new Date(lastUpdated).getMonth()
        const popupYear = new Date(lastUpdated).getFullYear()

        // hide the popup if it was closed in the current month
        if (month === popupMonth && year === popupYear) {
          isVisible = false
        }
      }

      if (isVisible) {
        openModal('tossInModal', { priority })
        return true
      }
    }

    const verifyShippingAddressModal = (priority: number) => {
      const { isShippingAddressInvalid, isShippingAddressUndeliverable } = subscription

      if (isMainPage && isShippingAddressInvalid) {
        openModal('verifyShippingAddressModal', { isUndeliverable: isShippingAddressUndeliverable, priority })

        return true
      }
    }

    const tryTwoThreeFragrancesModal = (priority: number) => {
      const lastUpdated = shownPopups?.tryTwoThreeFragrancesModal?.lastUpdated

      const isVisible = (
        subscription?.isUpgradableFromMonthlyPlan
        && subscription?.credits?.free === 0
        && getElapsedTime(subscription.subscriptionDate, 'days') >= 1 // 1 day after subscription
        && (
          !lastUpdated
          || getElapsedTime(lastUpdated, 'days') >= 30
        )
      )

      if (isVisible) {
        const onClose = () => {
          setPopupShown('tryTwoThreeFragrancesModal')
        }

        if (isQueueUpgradePopupEnabled) {
          openModal('addToQueueUpgradeModal', { priority, onClose })
        }
        else {
          openModal('tryTwoThreeFragrancesModal', { priority, onClose })
        }

        return true
      }
    }

    const resubscribePromoModal = (priority: number) => {
      const isVisible = (
        (isMainPage || pathname === links.subscription.perfumes || pathname === links.subscription.colognes)
        && subscription.isCancelled
        && !cookieStorage.getItem(constants.cookieNames.isResubscribePromoModalShown)
      )

      if (isVisible) {
        openModal('resubscribePromoModal', { priority })
        return true
      }
    }

    const mobileAppModal = (priority: number) => {
      const isVisible = (
        !shownPopups?.mobileAppModal?.lastUpdated
        && isMobile
      )

      if (isVisible) {
        openModal('mobileAppModal', { priority })
        return true
      }
    }

    const featuredBrandModal = (priority: number) => {
      const gender = user?.gender
      const isVisibleForGender = strapiWSTOModalWithProductsOptions && gender ? strapiWSTOModalWithProductsOptions.genders.includes(gender) : true

      const isVisible = (
        isStrapiWSTOEnabled
        && isVisibleForGender
        && !shownPopups?.[`featuredBrandModal-${strapiWSTOBrandSlug}`]?.lastUpdated
      )

      if (isVisible) {
        openModal('featuredBrandModal', { priority })
        return true
      }
    }

    const driftEcommercePromoModal = (priority: number) => {
      const isVisible = (
        isDriftEcommercePromoEnabled
        && !isDriftSubscriptionEnabled
        && !shownPopups?.['driftPromoModal']?.lastUpdated
      )

      if (isVisible) {
        openModal('driftPromoModal', { priority })
        return true
      }
    }

    const collaborationModal = (priority: number) => {
      if (!isSaksCollaborationEnabled) {
        return
      }

      if (!shownPopups?.scentbirdSelectModal?.lastUpdated) {
        openModal('scentbirdSelectModal', { priority })
        return true
      }
    }

    const limitedDropModal = (priority: number) => {
      const isVisible = (
        isLimitedDropsEnabled
        && limitedDrop?.status === 'LIVE'
        && !shownPopups?.[`limitedDropModal-${limitedDrop?.uid}`]?.lastUpdated
      )

      if (isVisible) {
        openModal('limitedDropModal', { priority })
        return true
      }
    }

    const caseSubscriptionPromoModal = (priority: number) => {
      const lastUpdated = shownPopups?.caseSubscriptionPromoModal?.lastUpdated

      const isVisible = (
        isMainPage
        && caseSubscription?.isEnabled
        && !caseSubscription?.isSelected
        && (!lastUpdated || getElapsedTime(lastUpdated, 'months') >= 1)
      )

      if (isVisible) {
        openModal('caseSubscriptionPromoModal', { priority })
        return true
      }
    }

    const driftSubscriptionPromoModal = (priority: number) => {
      const lastUpdated = shownPopups?.driftSubscriptionPromoModal?.lastUpdated

      const isVisible = (
        isMainPage
        && isDriftSubscriptionEnabled
        && driftSubscription?.isEnabled
        && !driftSubscription?.isSelected
        && (!lastUpdated || getElapsedTime(lastUpdated, 'months') >= 1)
      )

      if (isVisible) {
        openModal('driftSubscriptionPromoModal', { priority })
        return true
      }
    }

    const continueMobileAppModal = (priority: number) => {
      const haveShown = Boolean(shownPopups?.continueMobileAppModal)

      const isVisible = (
        isMobileAppEntrypointModalEnabled
        && subscription?.isActive
        && !haveShown
        && isMobile
        && (
          isMainPage
          || pathname === links.subscription.perfumes
          || pathname === links.subscription.colognes
        )
      )
      if (isVisible) {
        openModal('continueMobileAppModal', { priority })
        return true
      }
    }

    const modalsOrder = [
      resubscribePromoModal,
      verifyShippingAddressModal,
      firstTimeVisitModal,
      cardUpdateModal,
      driftSubscriptionPromoModal,
      driftEcommercePromoModal,
      caseSubscriptionPromoModal,
      lastShipmentReviewModal,
      candleSubscriptionPromoModalShownCount === 0 && candleSubscriptionPromoModal,
      tryTwoThreeFragrancesModal,
      privateSaleModal,
      tossInModal,
      mobileAppModal,
      candleSubscriptionPromoModalShownCount > 0 && candleSubscriptionPromoModal,
      limitedDropModal,
      collaborationModal,
      featuredBrandModal,
      inviteModal,
      continueMobileAppModal,
    ]
      .filter(Boolean)

    modalsOrder.some((checker, index) => checker(index + 1))

    modalWasOpenRef.current = true
  }, [
    shownPopups,
    setPopupShown,
    subscription,
    user,
    isLoggedIn,
    pathname,
    privateSale,
    unratedProduct,
    limitedDrop,
    isMobile,
    isFetching,
    isDriftEcommercePromoEnabled,
    isUnratedProductFetching,
    isCandleSubscriptionEnabled,
    isDriftSubscriptionEnabled,
    isStrapiWSTOEnabled,
    isSaksCollaborationEnabled,
    isLimitedDropFetching,
    isLimitedDropsEnabled,
    candleSubscription?.isEnabled,
    candleSubscription?.isSelected,
    caseSubscription?.isEnabled,
    caseSubscription?.isSelected,
    driftSubscription?.isEnabled,
    driftSubscription?.isSelected,
    strapiWSTOBrandSlug,
    strapiWSTOModalWithProductsOptions,
    isMobileAppEntrypointModalEnabled,
    isQueueUpgradePopupEnabled,
    availableReferralInvites,
  ])

  return null
}


export default SubscriptionModals
